<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { DailyRecord } from '@/types/sector';
import { useUpsertRecord } from '@/composables/useSectorData';

const props = defineProps<{ record: DailyRecord }>();
const emit = defineEmits<{
  close: [];
  saved: [msg: string];
  error: [msg: string];
}>();

const first = ref(props.record.first);
const second = ref(props.record.second);
const third = ref(props.record.third);
const saving = ref(false);

const upsert = useUpsertRecord();

async function save() {
  if (!first.value.trim() || !second.value.trim() || !third.value.trim()) {
    emit('error', '请填写所有字段');
    return;
  }
  saving.value = true;
  try {
    await upsert.mutateAsync({
      date: props.record.date,
      first: first.value.trim(),
      second: second.value.trim(),
      third: third.value.trim(),
    });
    emit('saved', `${props.record.date} 数据已更新`);
  } catch (e) {
    emit('error', (e as Error).message ?? '保存失败');
  } finally {
    saving.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close');
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  document.body.style.overflow = 'hidden';
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = '';
});
</script>

<template>
  <div class="fixed inset-0 z-40 flex items-center justify-center p-4 animate-fade-in" @click.self="emit('close')">
    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
    <div class="card relative w-full max-w-md animate-slide-up shadow-elevated" role="dialog" aria-modal="true">
      <div class="mb-4 flex items-center justify-between">
        <h2 class="text-base font-semibold">✏️ 编辑 {{ record.date }}</h2>
        <button class="btn btn-ghost px-2 py-1 text-xs" @click="emit('close')">✕</button>
      </div>

      <div class="space-y-3">
        <div>
          <label class="label">第一名 <span class="badge ml-1">50%</span></label>
          <input v-model="first" type="text" class="input" maxlength="20" />
        </div>
        <div>
          <label class="label">第二名 <span class="badge ml-1">30%</span></label>
          <input v-model="second" type="text" class="input" maxlength="20" />
        </div>
        <div>
          <label class="label">第三名 <span class="badge ml-1">20%</span></label>
          <input v-model="third" type="text" class="input" maxlength="20" />
        </div>
      </div>

      <div class="mt-4 flex gap-2">
        <button class="btn btn-primary flex-1" :disabled="saving" @click="save">
          {{ saving ? '保存中...' : '保存修改' }}
        </button>
        <button class="btn btn-ghost flex-1" @click="emit('close')">取消</button>
      </div>
    </div>
  </div>
</template>
